import { Conversation } from "@/types/conversation/conversation";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ConversationSummaryHeroProps {
  conversation: Conversation;
  topics: string[];
  isLoading?: boolean;
}

/**
 * The page's first hero card: a single confident block leading with the
 * summary the owner came here to read, followed by topic chips.
 *
 * Replaces the inline `SummaryWithTopics` in the new full-page layout
 * with HG brand chrome (gradient border + brand-tinted header) so it
 * earns the visual weight it deserves. Topic chips drop the tenant
 * primaryColor fill in favour of the HG gradient — this card is product
 * chrome, not tenant chrome.
 */
export function ConversationSummaryHero({
  conversation,
  topics,
  isLoading,
}: ConversationSummaryHeroProps) {
  const summary =
    conversation?.details?.summary ||
    conversation?.details?.analysis_result?.brief_summary;
  const hasTopics = topics && topics.length > 0;

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 md:px-6 py-4 border-b border-[#4E9CFF]/10"
          style={{
            background:
              "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <GenieIcon
              name="chat"
              size={26}
              color="#4E9CFF"
              secondaryColor="#FF4DB8"
              gradientEnabled
              label="Conversation summary"
            />
            <h3 className="text-base md:text-lg font-bold text-gray-900">
              Conversation summary
            </h3>
          </div>
        </div>

        <div className="px-5 md:px-6 py-6">
          {isLoading ? (
            <div className="text-sm text-gray-500">Loading summary…</div>
          ) : (
            <>
              <p className="text-[15px] md:text-base text-gray-800 leading-relaxed whitespace-pre-line">
                {summary || "Summary not available."}
              </p>
              {hasTopics && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {topics.map((topic: string) => (
                    <span
                      key={topic}
                      className="px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-[#4E9CFF]/10 to-[#FF4DB8]/10 border border-[#4E9CFF]/20 text-gray-800"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
